import React from 'react';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { Field, ErrorMessage } from 'formik';
import Heading from '../../../common/Heading';
import formStyles from '../../../styles/forms.module.scss';
import s from './StepGreenPowerData.module.scss';
import {
  TextInput,
  RadioGroupInput,
  CheckboxInput,
  AccessibleDatepickerInput,
} from '../../../common/FormFields';
import InputErrorMessage from '../../../common/FormFields/InputErrorMessage';
import checkboxStyles from '../WindFacilityForm.module.scss';
import InfoBubble from '../../../common/InfoBubble/InfoBubble';
import InfoBox from '../../../common/InfoBox/InfoBox';

function StepGreenPowerData(props) {
  const { values, isGreenPowerConsumptionTooSmall } = props;
  const intl = useIntl();
  const namePrefix = 'greenPowerData.';
  const isControlAreaCustom =
    values.greenPowerData && values.greenPowerData.controlArea === 'custom';

  const linkToVerbundProductPortfolio =
    intl.locale === 'en'
      ? 'https://www.verbund.com/en-at'
      : 'https://www.verbund.com/de-at';

  return (
    <>
      <Heading level={2} className={s.heading}>
        <FormattedMessage id="offerWizard.stepGreenPowerData.title" />
      </Heading>
      <div className={formStyles.inputWithVerticalSpacing}>
        <div className={formStyles.inputPair}>
          <Field
            name="information.start"
            labelMsg="offerWizard.StepSomeInformation.start.label"
            component={AccessibleDatepickerInput}
            minDate={new Date()}
          />
          <Field
            name="information.end"
            labelMsg="offerWizard.StepSomeInformation.end.label"
            component={AccessibleDatepickerInput}
            minDate={() => {
              if (values.information && values.information.start) {
                return new Date(values.information.start);
              }
              return new Date();
            }}
          />
        </div>
        <div className={checkboxStyles.checkBoxWithInfoIcon}>
          <Field
            name="greenPowerData.swap"
            labelContent={
              <FormattedMessage id="offerWizard.stepPowerType.gruenstromSwap" />
            }
            component={CheckboxInput}
          />
          <div className={checkboxStyles.infoIcon}>
            <InfoBubble>
              <FormattedMessage id="offerWizard.stepPowerType.gruenstromSwap.infoBubble" />
            </InfoBubble>
          </div>
        </div>
      </div>
      <div className={formStyles.inputWithVerticalSpacing}>
        <fieldset>
          <legend className={s.groupLabel}>
            <FormattedMessage id="offerWizard.stepGreenPowerData.preferredPowerGeneration" />
          </legend>
          <div className={s.generationRadios}>
            <Field
              name={`${namePrefix}wind`}
              labelContent={
                <FormattedMessage id="offerWizard.stepFacilityType.wind" />
              }
              component={CheckboxInput}
            />
            <Field
              name={`${namePrefix}water`}
              labelContent={
                <FormattedMessage id="offerWizard.stepFacilityType.wasser" />
              }
              component={CheckboxInput}
            />
            <Field
              name={`${namePrefix}photovoltaic`}
              labelContent={
                <FormattedMessage id="offerWizard.stepFacilityType.photovoltaik" />
              }
              component={CheckboxInput}
            />
            <Field
              name={`${namePrefix}biomass`}
              labelContent={
                <FormattedMessage id="offerWizard.stepFacilityType.biomasse" />
              }
              component={CheckboxInput}
            />
          </div>
        </fieldset>
        <ErrorMessage
          name={`${namePrefix}powerGeneration`}
          component={InputErrorMessage}
        />
      </div>
      <div className={formStyles.inputPairSingle}>
        <Field
          name={`${namePrefix}consumption`}
          component={TextInput}
          labelMsg="offerWizard.stepGreenPowerData.consumption.label"
        />
      </div>
      <div role="alert">
        {isGreenPowerConsumptionTooSmall && (
          <InfoBox>
            <Heading level={4}>
              <FormattedMessage id="offerWizard.stepGreenPowerData.isConsumptionTooSmall.title" />
            </Heading>
            <p className={s.consumptionTooSmallText}>
              <FormattedMessage id="offerWizard.stepGreenPowerData.isConsumptionTooSmall.text1" />{' '}
              <a
                href={linkToVerbundProductPortfolio}
                target="_blank"
                rel="noreferrer"
              >
                <FormattedMessage id="offerWizard.stepGreenPowerData.isConsumptionTooSmall.link" />
              </a>
            </p>
          </InfoBox>
        )}
      </div>
      <div className={formStyles.inputWithVerticalSpacing}>
        <Field
          captionMsg="offerWizard.stepGreenPowerData.certification"
          name={`${namePrefix}certification`}
          options={[
            {
              value: 'ee',
              labelText: 'TÜV Süd EE',
              infoText: intl.formatMessage({
                id: 'offerWizard.stepGreenPowerData.certification.TUEVEE',
              }),
            },
            {
              value: 'ee_plus',
              labelText: 'TÜV Süd EE+',
              infoText: intl.formatMessage({
                id: 'offerWizard.stepGreenPowerData.certification.TUEVEEPlus',
              }),
            },
            {
              value: 'ee_new',
              labelText: 'TÜV Süd EE neu',
              infoText: intl.formatMessage({
                id: 'offerWizard.stepGreenPowerData.certification.TUEVEENeu',
              }),
            },
            {
              value: 'none',
              labelText: intl.formatMessage({
                id: 'offerWizard.stepGreenPowerData.certification.none',
              }),
            },
          ]}
          component={RadioGroupInput}
          radioButtonsWrapperClass={s.certificationRadios}
        />
      </div>
      <div className={formStyles.inputPairSingle}>
        <Field
          name={`${namePrefix}label`}
          component={TextInput}
          labelMsg="offerWizard.stepGreenPowerData.label.label"
        />
      </div>
      <Field
        captionMsg="offerWizard.stepGreenPowerData.origin"
        name={`${namePrefix}regionOfOrigin`}
        options={[
          {
            value: 'de',
            labelText: 'Deutschland',
          },
          {
            value: 'de_at_ch',
            labelText: 'D-A-CH',
          },
          {
            value: 'eu',
            labelText: 'Europa',
          },
          {
            value: 'at',
            labelText: 'Österreich',
          },
          {
            value: 'de_at_ch_fr',
            labelText: 'D-A-CH + Frankreich',
          },
          {
            value: 'no_specification',
            labelText: intl.formatMessage({
              id: 'offerWizard.stepGreenPowerData.origin.noSpecification',
            }),
          },
        ]}
        component={RadioGroupInput}
        radioButtonsWrapperClass={s.originRadios}
      />
      <div className={formStyles.inputPairSingle}>
        <Field
          name={`${namePrefix}numberOfMeterPoints`}
          component={TextInput}
          labelMsg="offerWizard.stepGreenPowerData.numberOfMeterPoints.label"
        />
      </div>
      <div className={formStyles.inputWithVerticalSpacing}>
        <Field
          captionMsg="offerWizard.stepGreenPowerData.controlArea"
          name={`${namePrefix}controlArea`}
          options={[
            {
              value: 'apg',
              labelText: 'APG',
            },
            {
              value: 'tennet',
              labelText: 'TenneT',
            },
            {
              value: '50hertz',
              labelText: '50 Hertz',
            },
            {
              value: 'amprion',
              labelText: 'Amprion',
            },
            {
              value: 'transnetBW',
              labelText: 'Transnet BW',
            },
            {
              value: 'custom',
              labelText: intl.formatMessage({
                id: 'offerWizard.stepGreenPowerData.controlArea.other',
              }),
            },
          ]}
          component={RadioGroupInput}
          radioButtonsWrapperClass={s.controlAreaRadios}
        />
        {isControlAreaCustom && (
          <div className={formStyles.inputPairSingle}>
            <Field
              name={`${namePrefix}customControlArea`}
              component={TextInput}
              labelMsg="offerWizard.stepGreenPowerData.controlArea.otherTextInput"
              required
            />
          </div>
        )}
      </div>
      <div className={formStyles.inputWithVerticalSpacing}>
        <Field
          captionMsg="offerWizard.stepGreenPowerData.product"
          name={`${namePrefix}product`}
          options={[
            {
              value: 'fahrplan',
              labelText: 'Fahrplan',
            },
            {
              value: 'standardprodukt',
              labelText: 'Standardprodukt',
            },
          ]}
          component={RadioGroupInput}
          radioButtonsWrapperClass={s.productRadios}
        />
      </div>
    </>
  );
}

StepGreenPowerData.propTypes = {
  values: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    information: PropTypes.object,
    greenPowerData: PropTypes.shape({
      swap: PropTypes.bool,
      controlArea: PropTypes.string,
    }).isRequired,
  }).isRequired,
  isGreenPowerConsumptionTooSmall: PropTypes.bool.isRequired,
};

export default injectIntl(StepGreenPowerData);
